import React, { useState, useEffect } from 'react'
import { isInAppBrowser, openInExternalBrowser, useCopyToClipboard } from 'utils'

const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
)

/**
 * Shared Google OAuth login button with built-in in-app browser detection.
 *
 * @param {string} role - Required. 'teacher' or 'student'
 * @param {string} [label] - Button text
 * @param {string} [source] - Tracking source param (e.g. 'referral_supply')
 * @param {Object} [extraParams] - Additional query params (e.g. { team_invite_code: 'ABC' })
 * @param {Function} [onSubmit] - Callback before form submission
 * @param {string} [className] - Additional CSS classes for the button
 * @param {Object} [translations] - i18n overrides for warning text
 */
export default function GoogleLoginButton({ role, label, source, extraParams, onSubmit, className, style, translations, children, showWarning = true }) {
  const [csrfToken, setCsrfToken] = useState('')
  const inApp = isInAppBrowser()
  const { copyToClipboard, isCopied } = useCopyToClipboard()
  const t = translations || {}

  useEffect(() => {
    const token = document.querySelector('[name="csrf-token"]')?.content
    if (token) setCsrfToken(token)
  }, [])

  // Build action URL — only whitelisted extra params allowed
  const ALLOWED_EXTRA_PARAMS = new Set(['redirect_url', 'team_invite_code'])
  const params = new URLSearchParams({ role })
  if (source) params.set('source', source)
  if (extraParams) {
    Object.entries(extraParams).forEach(([k, v]) => {
      if (v != null && ALLOWED_EXTRA_PARAMS.has(k)) params.set(k, v)
    })
  }
  const actionUrl = `/users/auth/google_oauth2?${params.toString()}`

  return (
    <div>
      <form
        action={actionUrl}
        method="post"
        className="w-full"
        onSubmit={(e) => {
          if (onSubmit) onSubmit()
          if (inApp) { e.preventDefault(); return }
        }}
      >
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        <button
          type="submit"
          disabled={inApp}
          className={children
            ? `${className || ''} ${inApp ? 'opacity-50 cursor-not-allowed' : ''}`
            : `w-full flex items-center px-4 py-3 rounded-lg border transition-all duration-200 group ${
                inApp
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400 hover:shadow-sm'
              } ${className || ''}`
          }
          style={style}
        >
          {children || (
            <>
              <GoogleIcon className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium flex-1 text-center">
                {label || (role === 'teacher' ? "👨‍🏫 I'm a Teacher" : "👨‍🎓 I'm a Student")}
              </span>
            </>
          )}
        </button>
      </form>

      {inApp && showWarning && (
        <InAppBrowserWarning translations={t} copyToClipboard={copyToClipboard} isCopied={isCopied} />
      )}
    </div>
  )
}

/**
 * Inline in-app browser warning block (amber).
 * Used inside GoogleLoginButton. For modal version, see InAppBrowserWarningModal.
 */
export function InAppBrowserWarning({ translations, copyToClipboard, isCopied }) {
  const raw = translations || {}
  const t = {
    in_app_warning_title: raw.in_app_warning_title || raw.modal_title,
    in_app_warning_hint: raw.in_app_warning_hint || raw.paste_hint,
    in_app_open_browser: raw.in_app_open_browser || raw.open_browser,
    in_app_copy_url: raw.in_app_copy_url || raw.copy_url,
    in_app_copied_hint: raw.in_app_copied_hint || raw.copied_hint,
  }

  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-start gap-2">
        <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <div className="flex-1">
          <p className="text-xs font-medium text-amber-800 mb-1">
            {t.in_app_warning_title || 'Cannot use Google login in this browser'}
          </p>
          <p className="text-xs text-amber-600 mb-2">
            {t.in_app_warning_hint || 'Copy the URL and open in Safari or Chrome'}
          </p>
          <button
            onClick={async () => {
              const result = await openInExternalBrowser()
              if (result === null) copyToClipboard(window.location.href)
            }}
            className="w-full py-2 rounded-lg font-medium text-white text-xs transition-colors cursor-pointer"
            style={{ backgroundColor: isCopied ? '#10B981' : '#3366FF' }}
          >
            {isCopied
              ? (t.in_app_copied_hint || 'Copied! Paste in Safari or Chrome')
              : (t.in_app_open_browser || 'Open in Browser')}
          </button>
          {!isCopied && (
            <button
              onClick={() => copyToClipboard(window.location.href)}
              className="w-full mt-1.5 py-1.5 text-xs text-amber-700 hover:text-amber-900 transition-colors cursor-pointer"
            >
              {t.in_app_copy_url || 'Copy URL'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
